// Game logic utilities

export function formatElapsed(milliseconds) {
  const hundredths = Math.floor(Math.max(0, milliseconds || 0) / 10)
  return `${Math.floor(hundredths / 6000)}:${String(Math.floor(hundredths / 100) % 60).padStart(2, '0')}.${String(hundredths % 100).padStart(2, '0')}`
}

export function generateTimedShareText(run) {
  const grid = run.placements.map(p => p.correct ? '🟢' : '🔴').join(' ')
  return `EventPro ${run.date}\n${run.accuracy}/4 correct · ${formatElapsed(run.elapsed_ms)}\n${grid}`
}

export async function requestGame(endpoint, playerName, body) {
  const separator = endpoint.includes('?') ? '&' : '?'
  const response = await fetch(`${getApiUrl()}/eventpro/${endpoint}${separator}player_name=${encodeURIComponent(playerName || '')}`, {
    method: body === undefined ? 'GET' : 'POST',
    headers: { ...hubHeaders(), ...(body === undefined ? {} : { 'Content-Type': 'application/json' }) },
    ...(body === undefined ? {} : { body: JSON.stringify({ ...body, player_name: playerName }) }),
  })
  let data
  try { data = await response.json() }
  catch (_) { throw new Error('The game could not be reached. Please try again.') }
  if (!response.ok) throw new Error(data.error || 'The request failed. Please try again.')
  return data
}

// TEST MODE: Set to true to simulate Sunday for testing
const TEST_MODE_SUNDAY = false

export function getApiUrl() {
  if (process.env.REACT_APP_API_BASE_URL) return process.env.REACT_APP_API_BASE_URL
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:8003/api'
  }
  return `https://${window.location.hostname}/api`
}

export function hubHeaders() {
  const token = new URLSearchParams(window.location.search).get('hub_token') || localStorage.getItem('hubToken') || ''
  if (token) localStorage.setItem('hubToken', token)
  return token ? { 'X-Hub-Token': token } : {}
}

export function getHomeUrl() {
  if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
    return 'http://localhost:3000'
  }
  return 'https://games.precisionpros.com'
}

export const DAILY_CREDIT = 50
export const DAY_MULTIPLIER = { weekday: 1, sunday: 2 }

const MULTIPLIERS = {
  4: 1,
  3: 0.5,
  2: 0,
  1: -0.5,
  0: -1,
}

export function formatYear(year) {
  if (year < 0) {
    return `${Math.abs(year)} BC`
  }
  return year.toString()
}

export function isSunday() {
  if (TEST_MODE_SUNDAY) {
    return true
  }
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    weekday: 'long'
  })
  const weekday = formatter.format(new Date())
  return weekday === 'Sunday'
}

export function calcDailyPayout(wager, correct, startingBank) {
  const scoreMultiplier = MULTIPLIERS[correct]
  const dayMultiplier = isSunday() ? DAY_MULTIPLIER.sunday : DAY_MULTIPLIER.weekday
  const payout = wager * scoreMultiplier * dayMultiplier

  return startingBank + payout
}

export function findCorrectZoneIndex(events, eventToPlace, anchorYear) {
  // Find the correct zone for an event
  // Negative zones = before anchor, Positive zones = after anchor

  if (eventToPlace.year < anchorYear) {
    // Event goes before anchor - calculate position among before-anchor events
    const beforeAnchorEvents = events.filter(e => e.year < anchorYear).sort((a, b) => b.year - a.year)

    // If no events placed yet before anchor, use zone 0
    if (beforeAnchorEvents.length === 0) {
      return 0
    }

    for (let i = 0; i < beforeAnchorEvents.length; i++) {
      if (eventToPlace.year > beforeAnchorEvents[i].year) {
        // Event is newer than position i, insert here
        return -(i + 1)
      }
    }

    // Event is older than all before-anchor events
    return -(beforeAnchorEvents.length + 1)
  }

  // Event goes after anchor (year >= anchorYear)
  const afterAnchorEvents = events.filter(e => e.year >= anchorYear).sort((a, b) => b.year - a.year)

  for (let i = 0; i < afterAnchorEvents.length; i++) {
    if (eventToPlace.year > afterAnchorEvents[i].year) {
      // Event is newer than current position, insert here
      return afterAnchorEvents.length - i + 1
    }
  }

  // Event is older than all existing after-anchor events
  return 1
}

export function generateShareText(dayNo, dayPayout, strikes, weeklyBank) {
  const grid = strikes.map((s) => (s ? '🔴' : '🟢')).join(' ')
  return `EventPro #${dayNo}\n💰 Daily: $${dayPayout.toFixed(2)}\n${grid}\n🏆 Weekly: $${weeklyBank.toFixed(2)}`
}

// Get current week key (Monday of current week in Phoenix TZ)
export function getWeekKey() {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    weekday: 'long'
  })
  const parts = formatter.formatToParts(new Date())
  const year = parseInt(parts.find(p => p.type === 'year').value)
  const month = parseInt(parts.find(p => p.type === 'month').value)
  const day = parseInt(parts.find(p => p.type === 'day').value)
  const weekday = parts.find(p => p.type === 'weekday').value

  const d = new Date(year, month - 1, day)
  const dayOfWeek = d.getDay()
  const diff = d.getDate() - dayOfWeek + (dayOfWeek === 0 ? -6 : 1)
  d.setDate(diff)

  const resultFormatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const resultParts = resultFormatter.formatToParts(d)
  const resultYear = resultParts.find(p => p.type === 'year').value
  const resultMonth = resultParts.find(p => p.type === 'month').value
  const resultDay = resultParts.find(p => p.type === 'day').value
  return `${resultYear}-${resultMonth}-${resultDay}`
}

// Get today's date string in Phoenix timezone
export function getTodayString() {
  const formatter = new Intl.DateTimeFormat('en-US', {
    timeZone: 'America/Phoenix',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit'
  })
  const parts = formatter.formatToParts(new Date())
  const year = parts.find(p => p.type === 'year').value
  const month = parts.find(p => p.type === 'month').value
  const day = parts.find(p => p.type === 'day').value
  return `${year}-${month}-${day}`
}

// Check if game was played today
export function hasPlayedToday() {
  const weekKey = getWeekKey()
  const stored = localStorage.getItem(`eventpro_week_${weekKey}`)
  if (!stored) return false

  const weekData = JSON.parse(stored)
  const todayStr = getTodayString()
  return weekData.dailyResults?.some((r) => r.date === todayStr)
}

// Load week data from localStorage
export function loadWeekData() {
  const weekKey = getWeekKey()
  const stored = localStorage.getItem(`eventpro_week_${weekKey}`)

  if (!stored) {
    return {
      weeklyBank: 0,
      dailyResults: [],
      sundayCompleted: false,
    }
  }

  return JSON.parse(stored)
}

// Save week data to localStorage
export function saveWeekData(weekData) {
  const weekKey = getWeekKey()
  localStorage.setItem(`eventpro_week_${weekKey}`, JSON.stringify(weekData))
}

// Save a daily result
export function saveDailyResult(wager, accuracy, payout, strikes, weeklyBank, placedEventIds = []) {
  const weekData = loadWeekData()
  const todayStr = getTodayString()

  // Remove today's result if it exists (shouldn't, but just in case)
  weekData.dailyResults = weekData.dailyResults.filter((r) => r.date !== todayStr)

  // Add new result
  weekData.dailyResults.push({
    date: todayStr,
    accuracy,
    wager,
    payout,
    strikes,
    placedEventIds,
  })

  weekData.weeklyBank = weeklyBank

  saveWeekData(weekData)

  // Also save to backend database
  const playerName = localStorage.getItem('playerName')
  if (playerName) {
    const apiBaseUrl = getApiUrl()
    const resultData = {
      player_name: playerName,
      date: todayStr,
      wager: parseFloat(wager),
      accuracy,
      payout: parseFloat(payout),
      weekly_bank: parseFloat(weeklyBank),
      strikes,
      placed_event_ids: placedEventIds,
    }
    fetch(`${apiBaseUrl}/eventpro/save-result/`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...hubHeaders() },
      body: JSON.stringify(resultData),
    })
      .then(res => {
        if (!res.ok) {
          return res.json().then(err => {
            console.error('Failed to save result:', err)
          })
        }
        return res.json()
      })
      .catch((err) => console.error('Failed to save result to backend:', err))
  }
}
